import { api } from './api';
import { BACKEND_URL as BACKEND } from './config';
import { getAccessToken } from './session';

/* CUTOVER (Vague 2) : documents PDF + ingest tapent le backend apps/api ; les */
/* lectures (list/pages/search/title) passent par les ports tRPC staff (api.docs.*). */
/* Token (backend-issued) pour les routes REST du backend. */
const authHeaders = (token: string) => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });

export interface DocRow {
  id: string;
  title: string | null;
  source_url: string | null;
  page_count: number | null;
  ocr_status: 'pending' | 'processing' | 'done' | 'failed';
  ocr_error: string | null;
  created_at: string;
  ocr_done_at: string | null;
}

export interface DocPage {
  page_number: number;
  ocr_text: string | null;
}

export interface SearchHit {
  document_id: string;
  title: string | null;
  page_number: number;
  rank: number;
  snippet: string;
}

/* CLEAN-ARCH (Vague 4) : lectures documents via les ports tRPC staff (api.docs.*). */
export async function listDocuments(): Promise<DocRow[]> {
  return (await api.docs.list.query()) as DocRow[];
}

export async function getDocumentTitle(id: string): Promise<string | null> {
  return api.docs.title.query({ id });
}

export async function listPages(documentId: string): Promise<DocPage[]> {
  return (await api.docs.pages.query({ documentId })) as DocPage[];
}

export async function searchPages(q: string): Promise<SearchHit[]> {
  return (await api.docs.search.query({ q })) as SearchHit[];
}

/** Lazily ensure the document's real PDF is stored, returning a URL to display
 *  it (signed URL to the stored copy, or the original source_url as fallback,
 *  or null if neither is available). Best-effort; never throws. */
export async function ensureDocumentPdf(documentId: string): Promise<string | null> {
  const token = await getAccessToken();
  if (!token) return null;
  try {
    const res = await fetch(`${BACKEND}/documents/pdf`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ document_id: documentId }),
    });
    if (!res.ok) return null;
    const j = (await res.json()) as { signedUrl?: string; fallback?: string | null };
    return j.signedUrl ?? j.fallback ?? null;
  } catch {
    return null;
  }
}

/** Ingest a PDF by URL via the ingest-document Edge Function (admin JWT).
 *  Synchronous: resolves once OCR + title are done. */
export async function ingestDocument(url: string): Promise<{ id: string; title: string; page_count: number }> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${BACKEND}/documents/ingest`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ url }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.detail || body?.error || `ingest failed (${res.status})`);
  return body;
}
